
import React, { useState } from 'react';
import { X, Check, ShieldCheck, Zap, BookOpen, Star, Loader2, Sparkles } from 'lucide-react';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: (plan: 'monthly' | 'yearly') => Promise<void>;
  language?: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, onCheckout, language }) => {
  const [loading, setLoading] = useState<'monthly' | 'yearly' | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const isVi = language === 'vietnamese';

  if (!isOpen) return null;

  const handleCheckout = async (plan: 'monthly' | 'yearly') => {
    setLoading(plan);
    try {
      await onCheckout(plan);
    } finally {
      setLoading(null);
    }
  };

  const features = isVi ? [
    { icon: <Zap size={14} />, title: 'AI không giới hạn', desc: 'AI Check, Quiz, Câu hỏi học tập — dùng thoải mái' },
    { icon: <BookOpen size={14} />, title: 'Sổ tay không giới hạn', desc: 'Tạo bao nhiêu chủ đề tùy thích' },
    { icon: <ShieldCheck size={14} />, title: 'Đồng bộ đám mây', desc: 'Backup Google Drive + GitHub tự động' },
    { icon: <Sparkles size={14} />, title: 'Tất cả tính năng cao cấp', desc: 'Voice Tutor, PDF AI, Semantic Search...' },
  ] : [
    { icon: <Zap size={14} />, title: 'Unlimited AI actions', desc: 'AI Check, Quiz, Study Questions — use freely' },
    { icon: <BookOpen size={14} />, title: 'Unlimited notebooks', desc: 'Create as many subjects as you need' },
    { icon: <ShieldCheck size={14} />, title: 'Cloud sync', desc: 'Google Drive + GitHub automatic backup' },
    { icon: <Sparkles size={14} />, title: 'All premium features', desc: 'Voice Tutor, PDF AI, Semantic Search...' },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-3xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left — Benefits */}
        <div className="w-full md:w-2/5 bg-gradient-to-br from-indigo-600 to-blue-700 p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-[50px]" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-[60px]" />
          </div>
          <div className="relative z-10 h-full flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-white/20">
                <Star size={12} fill="white" /> Premium
              </div>
              <h2 className="text-3xl font-black mb-2">{isVi ? 'Nâng cấp lên Pro' : 'Upgrade to Pro'}</h2>
              <p className="text-blue-100 text-sm mb-8 leading-relaxed">
                {isVi
                  ? 'Mở khóa toàn bộ tiềm năng học tập với AI không giới hạn.'
                  : 'Unlock the full potential of AI-powered learning.'}
              </p>
              <div className="space-y-4">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="p-1 bg-blue-500/50 rounded-full mt-0.5">{f.icon}</div>
                    <div>
                      <h4 className="font-bold text-sm">{f.title}</h4>
                      <p className="text-xs text-blue-100 opacity-80">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/20 text-xs text-blue-100 opacity-70">
              {isVi ? 'Hủy bất cứ lúc nào. Không ràng buộc.' : 'Cancel anytime. No lock-in.'}
            </div>
          </div>
        </div>

        {/* Right — Plan selector */}
        <div className="flex-1 p-8 md:p-10 bg-white dark:bg-slate-900 relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>

          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {isVi ? 'Chọn gói của bạn' : 'Choose your plan'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {isVi ? 'Thanh toán qua Stripe — an toàn & bảo mật' : 'Powered by Stripe — secure & encrypted'}
          </p>

          <div className="space-y-3 mb-6">
            {/* Yearly */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`w-full p-4 rounded-2xl border-2 text-left transition-all relative ${
                selectedPlan === 'yearly'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40'
                  : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300'
              }`}
            >
              <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                {isVi ? 'Tiết kiệm 33%' : 'Save 33%'}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{isVi ? 'Hàng năm' : 'Yearly'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">$3.33/{isVi ? 'tháng' : 'mo'} · {isVi ? 'thanh toán hàng năm' : 'billed annually'}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-gray-900 dark:text-white">$39.99</p>
                  <p className="text-xs text-gray-400 line-through">$59.88</p>
                </div>
              </div>
              {selectedPlan === 'yearly' && (
                <div className="absolute top-1/2 -translate-y-1/2 right-4 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </button>

            {/* Monthly */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40'
                  : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{isVi ? 'Hàng tháng' : 'Monthly'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{isVi ? 'Hủy bất kỳ lúc nào' : 'Cancel anytime'}</p>
                </div>
                <p className="text-2xl font-black text-gray-900 dark:text-white">$4.99</p>
              </div>
              {selectedPlan === 'monthly' && (
                <div className="absolute top-1/2 -translate-y-1/2 right-4 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </button>
          </div>

          <button
            onClick={() => handleCheckout(selectedPlan)}
            disabled={!!loading}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/30 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {loading ? (
              <><Loader2 size={20} className="animate-spin" /> {isVi ? 'Đang chuyển hướng...' : 'Redirecting...'}</>
            ) : (
              isVi ? 'Tiếp tục thanh toán →' : 'Continue to checkout →'
            )}
          </button>

          <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
            <ShieldCheck size={12} />
            {isVi ? 'Thanh toán bảo mật qua Stripe. Tôi không lưu thông tin thẻ.' : 'Secure payment via Stripe. We never store card details.'}
          </p>
        </div>
      </div>
    </div>
  );
};
